import {TargetBlank} from '@wandb/weave/common/util/links';
import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {Slider} from '@wandb/weave/components';
import {Alert} from '@wandb/weave/components/Alert.styles';
import {Button} from '@wandb/weave/components/Button/Button';
import {Checkbox} from '@wandb/weave/components/Checkbox/Checkbox';
import * as Dialog from '@wandb/weave/components/Dialog';
import {Icon} from '@wandb/weave/components/Icon';
import {Loading} from '@wandb/weave/components/Loading';
import {Pill} from '@wandb/weave/components/Tag/Pill';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {extractErrorMessageFromApolloError} from '@wandb/weave/errors';
import numeral from 'numeral';
import React, {useCallback, useContext, useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import {twMerge} from 'tailwind-merge';

import {
  Banner,
  BannerFlexWrapper,
  BannerTextWrapper,
} from '../../../components/Banner';
import {AccountSelectorContext} from '../../../components/Search/SearchNav/AccountSelectorContextProvider';
import WBLogo from '../../../components/WBLogo';
import {
  CheckoutPreviewLineItemsMetadata,
  CheckoutPreviewQuery,
  Coupon,
  UsageType,
  useCheckoutPreviewQuery,
  useMakeOrgMembersNoAccessModelsSeatMutation,
  useTrialEndCouponInfoQuery,
  useUpgradeProCustomerMutation,
  WeaveIngestionLimitInMb,
} from '../../../generated/graphql';
import {Analytics} from '../../../services/analytics';
import {
  getModelSeatsSub,
  getPrimarySub,
  getWeaveCommitSub,
  getWeaveOverageSubBillingPeriod,
  isCommunityEditionPrimarySubscription,
  isPrimaryStripePlanInfoWithUnitPrice,
  isProPrimarySubscription,
  Org,
  OrganizationFlags,
  PrimaryStripePlanInfoWithUnitPrice,
  StripeElementsComp,
  useAccountPrivileges,
  useGetMinimumModelsSeatsToPurchase,
  useGetSubscriptionPlanByName,
  weaveCommitPlanNameToWeaveIngestionLimit,
} from '../../../util/accounts/pricing';
import {doNotRetryContext, propagateErrorsContext} from '../../../util/errors';
import history from '../../../util/history';
import {jsonParseSafe} from '../../../util/json';
import {
  historicUsageTab,
  privacyPolicy,
  termsOfService,
} from '../../../util/urls';
import {TABS} from '../../HistoricUsage/HistoricUsage';
import {useGetUsageTypeUsageThisBillingPeriod} from '../AccountSettings/BillingTab/CurrentBillingPeriodUsageSection';
import {getCouponDescription} from '../AccountSettings/BillingTab/PlanCard/PlanCardComponents';
import {PlanName} from '../util';
import {BillingAddressContextProvider} from './BillingAddressContext';
import {
  NewCheckoutModalContext,
  NewCheckoutModalUpdaterContext,
} from './NewCheckoutModalContext';
import {
  NewStripeForm,
  RenderButtonsParams,
  SubmitParams,
} from './NewStripeForm';
import {NumberInputWithRange} from './TeamsPlanTab';
import {useUpgradeSubscriptionToProPlan} from './util';

type CheckoutSectionProps = {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
};

const CheckoutSection: React.FC<CheckoutSectionProps> = ({
  title,
  isOpen,
  onToggle,
  children,
}) => {
  return (
    <div>
      <button
        className="flex w-full cursor-pointer items-center gap-8 py-8"
        onClick={onToggle}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            onToggle();
          }
        }}>
        <Icon
          name="chevron-next"
          className={twMerge(
            'text-16 transition-transform',
            isOpen ? 'rotate-90' : ''
          )}
        />
        <h3 className="text-24 text-gray-900 font-semibold">{title}</h3>
      </button>
      {isOpen && <div className="-mt-2 pl-[30px]">{children}</div>}
    </div>
  );
};

type ModelsSeatsSelectorProps = {
  modelSeats: number;
  setModelSeats: (seats: number) => void;
  minSeats: number;
};

const ModelsSeatsSelector: React.FC<ModelsSeatsSelectorProps> = ({
  modelSeats,
  setModelSeats,
  minSeats,
}) => {
  return (
    <div>
      <p className="text-[14px] text-moon-500">
        Add seats to enable logging runs and experiments with our wandb SDK.
      </p>
      <div className="mt-12 rounded-lg border border-moon-250 bg-white p-12">
        <div className="flex items-center justify-between">
          <p className="text-14 text-gray-900">Choose Models seats</p>
          <div className="flex items-center gap-12">
            <Pill label="1 seat free" color="teal" icon="lightboard" />
            <NumberInputWithRange
              min={minSeats}
              max={10}
              value={modelSeats}
              setValue={setModelSeats}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

type WeaveIngestionSelectorProps = {
  minWeaveIngestion: WeaveIngestionLimitInMb;
  weaveIngestion: WeaveIngestionLimitInMb;
  setWeaveIngestion: (limit: WeaveIngestionLimitInMb) => void;
  weaveUsage: number;
  orgName: string;
};

const WeaveIngestionSelector: React.FC<WeaveIngestionSelectorProps> = ({
  minWeaveIngestion,
  weaveIngestion,
  setWeaveIngestion,
  weaveUsage,
  orgName,
}) => {
  const availableLimits = [
    WeaveIngestionLimitInMb.Limit_1500,
    WeaveIngestionLimitInMb.Limit_2000,
    WeaveIngestionLimitInMb.Limit_3500,
    WeaveIngestionLimitInMb.Limit_5000,
    WeaveIngestionLimitInMb.Limit_6500,
    WeaveIngestionLimitInMb.Limit_8000,
    WeaveIngestionLimitInMb.Limit_10000,
    WeaveIngestionLimitInMb.Limit_12500,
    WeaveIngestionLimitInMb.Limit_15000,
    WeaveIngestionLimitInMb.Limit_20000,
    WeaveIngestionLimitInMb.Limit_25000,
    WeaveIngestionLimitInMb.Limit_30000,
    WeaveIngestionLimitInMb.Limit_35000,
  ];

  const minIndex = availableLimits.indexOf(minWeaveIngestion);
  const currentValue = availableLimits.indexOf(weaveIngestion);
  const handleWeaveIngestionChange = (value: number) => {
    if (value < minIndex) {
      setWeaveIngestion(availableLimits[minIndex]);
    } else {
      setWeaveIngestion(availableLimits[value]);
    }
  };

  const getLimitInMBFromWeaveIngestionLimit = (
    limit: WeaveIngestionLimitInMb
  ): number => {
    switch (limit) {
      case WeaveIngestionLimitInMb.Limit_1500:
        return 1500;
      case WeaveIngestionLimitInMb.Limit_2000:
        return 2000;
      case WeaveIngestionLimitInMb.Limit_3500:
        return 3500;
      case WeaveIngestionLimitInMb.Limit_5000:
        return 5000;
      case WeaveIngestionLimitInMb.Limit_6500:
        return 6500;
      case WeaveIngestionLimitInMb.Limit_8000:
        return 8000;
      case WeaveIngestionLimitInMb.Limit_10000:
        return 10000;
      case WeaveIngestionLimitInMb.Limit_12500:
        return 12500;
      case WeaveIngestionLimitInMb.Limit_15000:
        return 15000;
      case WeaveIngestionLimitInMb.Limit_20000:
        return 20000;
      case WeaveIngestionLimitInMb.Limit_25000:
        return 25000;
      case WeaveIngestionLimitInMb.Limit_30000:
        return 30000;
      case WeaveIngestionLimitInMb.Limit_35000:
        return 35000;
      default:
        return 1500; // Default to minimum limit
    }
  };

  const formatMBValue = (limit: WeaveIngestionLimitInMb): string => {
    const mb = getLimitInMBFromWeaveIngestionLimit(limit);
    if (mb >= 1000) {
      return `${mb / 1000}k`;
    }
    return mb.toString();
  };

  const formatWeaveLimitsValue = (value: number) =>
    numeral(value).format('0.0b');

  return (
    <div>
      <p className="text-[14px] text-moon-500">
        Add MBs to log traces and run evaluations with our weave SDK.
      </p>
      <div className="mt-12 rounded-lg border border-moon-250 bg-white p-12">
        <div className="grid grid-cols-[1fr_auto] items-center gap-12">
          <div className="relative pt-4">
            <Slider.Root
              min={0}
              max={availableLimits.length - 1}
              step={1}
              value={[currentValue]}
              onValueChange={([newVal]) => handleWeaveIngestionChange(newVal)}>
              <Slider.Track>
                <Slider.Range className="bg-teal-600" />
              </Slider.Track>
              <div className="absolute mb-[-36px] flex w-full justify-between">
                {availableLimits.map((limit, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <div
                      className={`h-2 w-[1px] ${
                        index < minIndex ? 'bg-gray-200' : 'bg-gray-300'
                      }`}
                    />
                    <span
                      className={`text-10 mt-1 ${
                        index < minIndex ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                      {formatMBValue(limit)}
                    </span>
                  </div>
                ))}
              </div>
              <Slider.Thumb />
            </Slider.Root>
          </div>
        </div>
        <div className="mt-30">
          <Alert severity="info" className="w-full">
            <div className="flex w-full items-center justify-between">
              <span className="flex items-center">
                <Icon name="lightboard" className="mr-8" />
                <span className="font-bold">
                  You used {formatWeaveLimitsValue(weaveUsage) ?? 0} so far this
                  month.
                </span>
                <span className="ml-4">
                  The first {formatMBValue(minWeaveIngestion)} mb are free.
                </span>
              </span>
              <span
                className="cursor-pointer font-bold"
                onClick={() => {
                  history.push(
                    historicUsageTab(
                      orgName,
                      TABS[UsageType.Weave]?.urlPart ?? ''
                    )
                  );
                }}>
                View usage
              </span>
            </div>
          </Alert>
        </div>
      </div>
    </div>
  );
};

type CheckoutSummaryProps = {
  isAlreadyPro: boolean;
  checkoutPreviewData?: CheckoutPreviewQuery;
  checkoutPreviewLoading: boolean;
  coupon?: Coupon | null;
  isTermsChecked: boolean;
  onTermsChange: (checked: boolean) => void;
};

const getLineItems = (
  isAlreadyPro: boolean,
  filteredItems: CheckoutPreviewLineItemsMetadata[]
) => {
  if (isAlreadyPro && filteredItems.length === 0) {
    // make fake CheckoutPreviewLineItemsMetadata when no changes
    return [
      {
        __typename: 'CheckoutPreviewLineItemsMetadata',
        description: 'No plan changes',
        amount: 0,
        discountAmount: null,
        position: 0,
        product: '',
        subLineItems: [],
      },
    ] as CheckoutPreviewLineItemsMetadata[];
  }

  if (isAlreadyPro) {
    return filteredItems;
  }

  // Sort items by position
  return [...filteredItems].sort((a, b) => a.position - b.position);
};

type LineItemProps = CheckoutPreviewLineItemsMetadata & {
  showItemDiscount: boolean;
  coupon?: Coupon | null;
};

const LineItem: React.FC<LineItemProps> = ({
  description,
  amount,
  discountAmount,
  product,
  subLineItems,
  showItemDiscount,
  coupon,
}) => {
  const originalPrice = amount / 100;
  const discount = discountAmount ?? 0;
  const discountedPrice = (amount - discount) / 100;

  return (
    <div className="mb-16">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <span className="text-14 text-gray-900 flex items-center font-semibold">
            {product}
            {showItemDiscount && coupon && (
              <Pill
                label={getCouponDescription(coupon)}
                color="gold"
                className="ml-8"
              />
            )}
          </span>
          {description && (
            <span className="text-12 text-moon-500">{description}</span>
          )}
        </div>
        <span className="text-14 text-gray-700 flex items-center font-semibold">
          {showItemDiscount ? (
            <>
              <s className="text-gray-400 mr-4">${originalPrice}</s>$
              {discountedPrice}
            </>
          ) : (
            `$${originalPrice}`
          )}
        </span>
      </div>
      {(subLineItems ?? []).length > 0 && (
        <div className="ml-16 mt-8">
          {subLineItems.map(
            (subItem, subIndex) =>
              subItem && (
                <div
                  key={subIndex}
                  className="text-11 text-gray-700 flex items-center justify-between">
                  <span>{subItem.description}</span>
                  <span>${subItem.amount / 100}</span>
                </div>
              )
          )}
        </div>
      )}
    </div>
  );
};

const CheckoutPlanSummary: React.FC<CheckoutSummaryProps> = ({
  isAlreadyPro,
  checkoutPreviewData,
  checkoutPreviewLoading,
  coupon,
  isTermsChecked,
  onTermsChange,
}) => {
  const filteredItems =
    checkoutPreviewData?.checkoutPreview?.lineItems?.filter(
      isNotNullOrUndefined
    ) ?? [];

  const discountAppliesToAll =
    filteredItems.length > 0 &&
    filteredItems.every(item => item.discountAmount != null) &&
    coupon != null;

  const totalDiscountAmount = discountAppliesToAll
    ? filteredItems.reduce((sum, item) => sum + (item.discountAmount ?? 0), 0)
    : 0;

  const lineItems = getLineItems(isAlreadyPro, filteredItems);

  const totalAmount = checkoutPreviewData?.checkoutPreview?.total ?? 0;
  const totalWithDiscount = totalAmount + totalDiscountAmount;
  const totalInDollars = totalAmount / 100;
  const totalWithDiscountInDollars = totalWithDiscount / 100;

  return (
    <div className="mx-5 flex flex-col rounded-lg border border-moon-250 bg-white">
      <div className="bg-gray-50 rounded-t-lg border-b border-moon-250 p-24">
        <h3 className="text-16 text-gray-900 font-semibold">
          Pro plan update summary
        </h3>
        <p className="text-14 text-moon-500">All amounts are shown in USD</p>
      </div>
      <div className="p-24">
        <div className="min-h-[50px]">
          {checkoutPreviewLoading ? (
            <div className="flex h-full items-center justify-center">
              <Loading />
            </div>
          ) : (
            lineItems.map((item, index) => {
              const showItemDiscount =
                !discountAppliesToAll &&
                coupon != null &&
                item.discountAmount != null;

              return (
                <LineItem
                  key={index}
                  {...item}
                  showItemDiscount={showItemDiscount}
                  coupon={coupon}
                />
              );
            })
          )}
        </div>
        <div className="text-gray-900 mt-12 flex items-center justify-between border-t border-moon-250 pt-12 font-semibold">
          <span className="flex items-center">
            Total due today
            {discountAppliesToAll && (
              <div className="ml-8">
                <Pill label={getCouponDescription(coupon)} color="gold" />
              </div>
            )}
          </span>
          <span>
            {discountAppliesToAll && (
              <s className="text-gray-400 mr-4">
                ${totalWithDiscountInDollars}
              </s>
            )}
            ${totalInDollars}
          </span>
        </div>
        <div className="-ml-12 flex flex-row items-center">
          <Checkbox
            style={{margin: 12}}
            checked={isTermsChecked}
            onClick={() => {
              onTermsChange(!isTermsChecked);
            }}
          />
          <span className="text-14 text-gray-700">
            I agree to the{' '}
            <TargetBlank href={termsOfService()}>
              <u>Terms of Service</u>
            </TargetBlank>{' '}
            and{' '}
            <TargetBlank href={privacyPolicy()}>
              <u>Privacy Policy</u>
            </TargetBlank>
            .
          </span>
        </div>
      </div>
    </div>
  );
};

const performExistingProPlanUpgrade = async (
  finalOrg: Org,
  finalSeats: number,
  finalWeaveIngestion: WeaveIngestionLimitInMb,
  onTransactionCompleted: () => void,
  setUpgrading: (upgrading: boolean) => void,
  updateProOrganizationMutation: ReturnType<
    typeof useUpgradeProCustomerMutation
  >,
  boughtExtraSeats: boolean,
  boughtExtraWeaveIngestion: boolean
) => {
  setUpgrading(true);
  try {
    const updateWeaveTierResults = await updateProOrganizationMutation[0]({
      variables: {
        organizationID: finalOrg.id,
        weaveIngestionTier: finalWeaveIngestion,
        modelsSeats: finalSeats,
      },
    });

    if (updateWeaveTierResults.errors) {
      toast(
        `Error updating weave subscription: ${updateWeaveTierResults.errors}`
      );
    }
  } catch (err) {
    const errMsg = extractErrorMessageFromApolloError(err);
    toast(`Error updating subscription: ${errMsg}`);
  } finally {
    Analytics.track('Pro Plan Upgrade', {
      orgId: finalOrg.id,
      orgName: finalOrg.name,
      seats: finalSeats,
      weaveIngestion: finalWeaveIngestion,
      boughtExtraSeats,
      boughtExtraWeaveIngestion,
    });
    setUpgrading(false);
    onTransactionCompleted();
  }
};

const performFirstTimePurchase = async (
  finalParams: SubmitParams,
  finalOrg: Org,
  finalSelectedPlan: PrimaryStripePlanInfoWithUnitPrice,
  finalSeats: number,
  finalWeaveIngestion: WeaveIngestionLimitInMb,
  isWeaveOnlyUser: boolean,
  onTransactionCompleted: () => void,
  upgradeSubscriptionToPro: ReturnType<typeof useUpgradeSubscriptionToProPlan>,
  makeOrgMembersNoAccessMutation: ReturnType<
    typeof useMakeOrgMembersNoAccessModelsSeatMutation
  >,
  isComingFromTrial: boolean
) => {
  const success = await upgradeSubscriptionToPro(
    finalParams,
    finalSelectedPlan,
    finalSeats,
    finalOrg,
    isWeaveOnlyUser,
    finalWeaveIngestion
  );
  if (!success) {
    return false;
  }
  if (!isWeaveOnlyUser) {
    Analytics.track('Pro Plan Purchase', {
      orgId: finalOrg.id,
      orgName: finalOrg.name,
      seats: finalSeats,
      weaveIngestion: finalWeaveIngestion,
      previousPlan: isComingFromTrial ? 'Pro Trial' : 'Free Plan',
      identifiedAsWeaveOnlyUser: isWeaveOnlyUser,
    });
    onTransactionCompleted();
    return true;
  }

  const {errors: makeOrgNoAccessErrors} =
    await makeOrgMembersNoAccessMutation[0]({
      variables: {organizationId: finalOrg.id},
    });

  if (makeOrgNoAccessErrors != null) {
    const errMsg = makeOrgNoAccessErrors.map(e => e.message).join(', ');
    console.error(`Error updating model seats for org members: ${errMsg}`);
    return false;
  }

  Analytics.track('Pro Plan Purchase', {
    orgId: finalOrg.id,
    orgName: finalOrg.name,
    seats: finalSeats,
    weaveIngestion: finalWeaveIngestion,
    previousPlan: isComingFromTrial ? 'Pro Trial' : 'Free Plan',
    identifiedAsWeaveOnlyUser: isWeaveOnlyUser,
  });

  onTransactionCompleted();
  return true;
};

type CustomizePlanModalProps = {
  isOpen: boolean;
  onClose: () => void;
  org: Org;
  onTransactionCompleted: () => void;
};

export const CustomizePlanModal: React.FC<CustomizePlanModalProps> = ({
  isOpen,
  onClose,
  org,
  onTransactionCompleted,
}) => {
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const {
    minModelsSeatsToPurchase,
    isWeaveOnlyUser,
    loading: minSeatsToPurchaseLoading,
  } = useGetMinimumModelsSeatsToPurchase(org.id, org.usedSeats);
  const {selectedAccount} = useContext(AccountSelectorContext);
  const [submitting, setSubmitting] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [isModelsSectionOpen, setIsModelsSectionOpen] = useState(true);
  const [isWeaveSectionOpen, setIsWeaveSectionOpen] = useState(true);
  const [stripeFormProps, setStripeFormProps] =
    useState<RenderButtonsParams | null>(null);
  const [stripeFormError, setStripeFormError] = useState<string | null>(null);

  const {loading: loadingPlans, plans} = useGetSubscriptionPlanByName();

  const proYearlyPlan = plans[PlanName.ProAnnual];

  const primarySub = getPrimarySub(org);
  const isComingFromTrial =
    primarySub?.plan != null &&
    isCommunityEditionPrimarySubscription({plan: {name: primarySub.plan.name}});
  const isAlreadyProPlan =
    primarySub?.plan != null &&
    isProPrimarySubscription({plan: {name: primarySub.plan.name}});

  const modelsSeatSub = getModelSeatsSub(org);

  const flags = org?.flags as string;
  const parsedOrgFlags =
    flags != null ? (jsonParseSafe(flags) as OrganizationFlags) : {};
  const orgHasRedeemedCoupon = parsedOrgFlags.coupon_id != null;

  const {data: couponData} = useTrialEndCouponInfoQuery({
    variables: {couponID: parsedOrgFlags.coupon_id as string},
    skip: !orgHasRedeemedCoupon,
    context: propagateErrorsContext(), // if the redeemed coupon is now invalid, we will just ignore the coupon
    fetchPolicy: 'no-cache', // always want the up to date info on the coupon when the user is about to purchase
  });
  const coupon = couponData?.coupon;

  let minSeats = minModelsSeatsToPurchase ?? 1;
  let minIngestion = WeaveIngestionLimitInMb.Limit_1500;
  // if org is already on Pro plan, this is an upgrade, so min-seats and weave ingestion should match existing values
  if (isAlreadyProPlan) {
    minSeats = modelsSeatSub?.seats ?? minSeats;
    const weaveSub = getWeaveCommitSub(org);
    const weaveIngestion = weaveCommitPlanNameToWeaveIngestionLimit(
      weaveSub?.plan?.name ?? ''
    );
    if (weaveIngestion != null) {
      minIngestion = weaveIngestion;
    }
  }
  const [modelSeats, setModelSeats] = useState(minSeats);

  const [weaveIngestion, setWeaveIngestion] =
    useState<WeaveIngestionLimitInMb>(minIngestion);

  const [minWeaveIngestion, setMinWeaveIngestion] =
    useState<WeaveIngestionLimitInMb>(minIngestion);

  const upgradeSubscriptionToPro = useUpgradeSubscriptionToProPlan();

  const updateProOrganizationMutation = useUpgradeProCustomerMutation({
    context: propagateErrorsContext(),
  });

  const makeOrgMembersNoAccessMutation =
    useMakeOrgMembersNoAccessModelsSeatMutation({
      context: propagateErrorsContext(),
    });

  useEffect(() => {
    if (isOpen && !isAlreadyProPlan) {
      if (isWeaveOnlyUser) {
        setIsModelsSectionOpen(false);
        setIsWeaveSectionOpen(true);
      } else {
        setIsModelsSectionOpen(true);
        setIsWeaveSectionOpen(false);
      }
    }
  }, [isOpen, isWeaveOnlyUser, isAlreadyProPlan]);

  const isOnProAndNoChangeToCurrentPlan =
    weaveIngestion === minIngestion &&
    modelSeats === minSeats &&
    isAlreadyProPlan;

  const {
    data: checkoutPreviewData,
    refetch: refetchCheckoutPreview,
    loading: checkoutPreviewLoading,
  } = useCheckoutPreviewQuery({
    variables: {
      organizationID: org.id,
      weaveIngestionTier: weaveIngestion,
      numberOfModelsSeat: modelSeats,
      couponID: coupon?.id,
    },
    // skip the checkout preview when already on Pro and no change to the current plan
    skip: minSeatsToPurchaseLoading || isOnProAndNoChangeToCurrentPlan,
    context: {...propagateErrorsContext(), ...doNotRetryContext()},
  });

  const handleModelSeatsChange = useCallback(
    (newSeats: number) => {
      setModelSeats(newSeats);
      if (refetchCheckoutPreview) {
        refetchCheckoutPreview();
      }
    },
    [refetchCheckoutPreview]
  );

  const handleWeaveIngestionChange = useCallback(
    (newLimit: WeaveIngestionLimitInMb) => {
      setWeaveIngestion(newLimit);
      if (refetchCheckoutPreview) {
        refetchCheckoutPreview();
      }
    },
    [refetchCheckoutPreview]
  );

  const {loading: privilegesLoading, privileges} =
    useAccountPrivileges(selectedAccount);

  const weaveOverageData = useGetUsageTypeUsageThisBillingPeriod(
    org.name,
    new Date(org.createdAt || Date.now()),
    UsageType.Weave,
    getWeaveOverageSubBillingPeriod(privileges, org),
    0,
    privilegesLoading
  );

  const handleStripeFormProps = useCallback((props: RenderButtonsParams) => {
    setStripeFormProps(props);
    return null;
  }, []);

  const stripeContextValue = {submitting};
  const stripeUpdaterContextValue = {setSubmitting};

  if (loadingPlans || minSeatsToPurchaseLoading) {
    return null;
  }

  if (
    proYearlyPlan == null ||
    !isPrimaryStripePlanInfoWithUnitPrice(proYearlyPlan)
  ) {
    toast(
      'Something went wrong while upgrading to a higher tier plan. Please reach out to support@wandb.com.'
    );
    return null;
  }

  return (
    <Tailwind>
      <Dialog.Root open={isOpen} onOpenChange={onClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed" />
          <Dialog.Content className="left-1/2 top-1/2 w-full max-w-screen-2xl -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex flex-col items-start">
                <WBLogo
                  data-test="wb-logo"
                  aria-label="Weights & Biases Logo"
                  className="rounded-full hover:bg-white"
                />
              </div>
              <Dialog.Close asChild>
                <Button
                  variant="ghost"
                  icon="close"
                  onClick={() => {
                    if (!submitting) {
                      onClose();
                    }
                  }}
                  disabled={submitting}
                  className="hover:bg-gray-100 rounded-full p-8">
                  <span className="sr-only">Close</span>
                </Button>
              </Dialog.Close>
            </div>
            <div className="mx-auto w-full">
              <div className="flex w-full justify-center gap-16">
                <div className="flex h-[670px] w-[700px] flex-col overflow-y-auto">
                  <Dialog.Title className="text-24 mb-12 font-semibold">
                    Customize your plan
                  </Dialog.Title>
                  <div className="mb-16 border-t border-moon-250"></div>
                  <div className="flex-shrink-0 divide-y divide-moon-250">
                    <div className="pb-16">
                      <CheckoutSection
                        title="W&B Models seats to train or fine-tune AI models"
                        isOpen={isModelsSectionOpen}
                        onToggle={() =>
                          setIsModelsSectionOpen(!isModelsSectionOpen)
                        }>
                        <ModelsSeatsSelector
                          modelSeats={modelSeats}
                          setModelSeats={handleModelSeatsChange}
                          minSeats={minSeats}
                        />
                      </CheckoutSection>
                    </div>

                    <div className="py-16">
                      <CheckoutSection
                        title="W&B Weave data ingestion (in MB/mo) to analyze and improve AI apps"
                        isOpen={isWeaveSectionOpen}
                        onToggle={() =>
                          setIsWeaveSectionOpen(!isWeaveSectionOpen)
                        }>
                        <WeaveIngestionSelector
                          minWeaveIngestion={minWeaveIngestion}
                          weaveIngestion={weaveIngestion}
                          setWeaveIngestion={handleWeaveIngestionChange}
                          weaveUsage={weaveOverageData.usage}
                          orgName={org.name}
                        />
                      </CheckoutSection>
                    </div>

                    <div className="pt-16"></div>
                  </div>

                  <div className="flex min-h-0 flex-1 flex-col pl-32">
                    <h3 className="text-24 mb-10 font-semibold">
                      Payment information
                    </h3>
                    <div className="min-h-0 flex-1">
                      <div className="rounded-lg border border-moon-250 bg-white p-6">
                        <BillingAddressContextProvider>
                          <NewCheckoutModalContext.Provider
                            value={stripeContextValue}>
                            <NewCheckoutModalUpdaterContext.Provider
                              value={stripeUpdaterContextValue}>
                              <StripeElementsComp>
                                <NewStripeForm
                                  orgName={org.name}
                                  orgID={org.id}
                                  renderButtons={handleStripeFormProps}
                                  onError={setStripeFormError}
                                />
                              </StripeElementsComp>
                            </NewCheckoutModalUpdaterContext.Provider>
                          </NewCheckoutModalContext.Provider>
                        </BillingAddressContextProvider>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-[600px] overflow-y-auto">
                  <CheckoutPlanSummary
                    isAlreadyPro={isAlreadyProPlan}
                    checkoutPreviewData={checkoutPreviewData}
                    checkoutPreviewLoading={checkoutPreviewLoading}
                    coupon={coupon}
                    isTermsChecked={isTermsChecked}
                    onTermsChange={setIsTermsChecked}
                  />
                  {stripeFormProps && (
                    <Button
                      variant="primary"
                      onClick={async () => {
                        if (!stripeFormProps) {
                          return;
                        }
                        if (isAlreadyProPlan) {
                          return performExistingProPlanUpgrade(
                            org,
                            modelSeats,
                            weaveIngestion,
                            onTransactionCompleted,
                            setUpgrading,
                            updateProOrganizationMutation,
                            modelSeats > minSeats,
                            weaveIngestion !== minIngestion
                          );
                        }
                        await stripeFormProps.submitWrapper(
                          async (finalParams: SubmitParams) => {
                            return performFirstTimePurchase(
                              finalParams,
                              org,
                              proYearlyPlan,
                              modelSeats,
                              weaveIngestion,
                              !!isWeaveOnlyUser,
                              onTransactionCompleted,
                              upgradeSubscriptionToPro,
                              makeOrgMembersNoAccessMutation,
                              isComingFromTrial
                            );
                          }
                        );
                      }}
                      disabled={
                        stripeFormProps.submitButtonDisabled ||
                        isOnProAndNoChangeToCurrentPlan ||
                        stripeFormProps.submitting ||
                        !isTermsChecked ||
                        upgrading
                      }
                      className="mt-16 w-full">
                      {stripeFormProps.submitting || upgrading
                        ? 'Processing...'
                        : 'Upgrade'}
                    </Button>
                  )}
                  {stripeFormError && (
                    <Banner variant="error" className="m-16 mx-5">
                      <BannerFlexWrapper>
                        <Icon name="warning" />
                        <BannerTextWrapper>{stripeFormError}</BannerTextWrapper>
                      </BannerFlexWrapper>
                    </Banner>
                  )}
                </div>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </Tailwind>
  );
};
